import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, SkipForward, ExternalLink } from 'lucide-react';

const TITLES = {
  missing_payer: 'Ohne Zahler — zuordnen',
  missing_family: 'Ohne Familie — zuordnen',
  missing_contract: 'Ohne Vertrag — anlegen',
};

const studentLabel = (s) => {
  if (!s) return '—';
  const kz = s.kassenzeichen ? ` (${s.kassenzeichen})` : '';
  return `${s.name || '—'}${kz}`;
};

export function DataQualityStudentWizard({ open, onClose, category, initialStudents = [], onFixed }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [tariffId, setTariffId] = useState('');

  const current = queue[index] || null;
  const remaining = Math.max(queue.length - index, 0);

  useEffect(() => {
    if (!open) return;
    setQueue([...(initialStudents || [])]);
    setIndex(0);
    setSearch('');
    setSearchHits([]);
    setTariffId('');
    if (category === 'missing_contract') {
      api.get('/tariffs-discounts/tariffs')
        .then((r) => setTariffs((r.data || []).filter((t) => t.is_active)))
        .catch(() => setTariffs([]));
    }
  }, [open, category, initialStudents]);

  useEffect(() => {
    if (!open || !current?.id) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/students/${current.id}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch(() => { if (!cancelled) { setDetail(null); toast.error('Schülerdaten konnten nicht geladen werden'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, current?.id]);

  const advance = () => {
    onFixed?.();
    if (index >= queue.length - 1) {
      toast.success('Liste abgeschlossen');
      onClose();
      return;
    }
    setIndex((i) => i + 1);
    setSearch('');
    setSearchHits([]);
    setTariffId('');
  };

  const skip = () => {
    if (index >= queue.length - 1) {
      onClose();
      return;
    }
    setIndex((i) => i + 1);
    setSearch('');
    setSearchHits([]);
    setTariffId('');
  };

  const markPayer = async (guardianId) => {
    setSaving(true);
    try {
      await api.put(`/guardians/${guardianId}`, { is_payer: true });
      toast.success('Als Zahler markiert');
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const linkSiblingPayer = async () => {
    setSaving(true);
    try {
      await api.post(`/students/${current.id}/link-family-payer`);
      toast.success('Zahler von Geschwister übernommen');
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kein Geschwister-Zahler gefunden');
    } finally {
      setSaving(false);
    }
  };

  const searchGuardians = async () => {
    const q = search.trim();
    if (q.length < 2) return;
    try {
      const r = await api.get('/guardians', { params: { search: q, limit: 20 } });
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.guardians || []);
      setSearchHits(items);
    } catch {
      toast.error('Suche fehlgeschlagen');
    }
  };

  const linkGuardian = async (guardianId) => {
    setSaving(true);
    try {
      await api.post(`/students/${current.id}/guardians/${guardianId}`, null, { params: { as_payer: true } });
      toast.success('Angehöriger verknüpft und als Zahler gesetzt');
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verknüpfung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const searchFamilies = async () => {
    const q = search.trim();
    if (q.length < 2) return;
    try {
      const r = await api.get('/families', { params: { search: q, page: 1, per_page: 20 } });
      const items = r.data?.items || [];
      setSearchHits(items);
    } catch {
      toast.error('Familiensuche fehlgeschlagen');
    }
  };

  const addToFamily = async (familyId) => {
    setSaving(true);
    try {
      await api.post(`/families/${familyId}/members/${current.id}`);
      toast.success('Zur Familie hinzugefügt');
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const createFamily = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.post('/families', {
        name: `Familie ${detail.last_name}`,
        primary_address: detail.address || null,
        primary_postal_code: detail.postal_code || null,
        primary_city: detail.city || null,
        primary_phone: detail.phone || null,
        primary_email: detail.email || null,
        member_ids: [detail.id],
      });
      toast.success('Familie angelegt');
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Familie konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  const suggestedTariffId = useMemo(() => {
    if (tariffId) return tariffId;
    return tariffs[0]?.id || '';
  }, [tariffId, tariffs]);

  const createContract = async () => {
    const tid = tariffId || suggestedTariffId;
    if (!tid) {
      toast.error('Bitte einen Tarif wählen');
      return;
    }
    const tariff = tariffs.find((t) => t.id === tid);
    setSaving(true);
    try {
      const r = await api.post('/contracts', {
        student_id: current.id,
        start_date: new Date().toISOString().slice(0, 10),
        status: 'active',
        auto_apply_discount: true,
        contract_lines: [{
          tariff_id: tid,
          description: tariff?.name || 'Unterricht',
          amount: Number(tariff?.amount ?? 0) || 0,
          quantity: 1,
        }],
      });
      toast.success(`Vertrag ${r.data.contract_number} erstellt`);
      advance();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vertrag konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  const guardians = detail?.guardians || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid={`dq-wizard-${category}`}>
        <DialogHeader>
          <DialogTitle>
            {TITLES[category] || 'Datenqualität'}
            <span className="ml-2 text-sm font-normal text-slate-500">
              {remaining > 0 ? `${index + 1} / ${queue.length}` : '0'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {!current ? (
          <p className="text-sm text-slate-500 py-6 text-center">Keine Einträge mehr.</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-sm font-semibold text-slate-900" data-testid="dq-wizard-student">
                {studentLabel(current)}
              </div>
              {detail && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {[detail.address, [detail.postal_code, detail.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'Keine Adresse'}
                </div>
              )}
              <Link to={`/students/${current.id}`}
                    className="inline-flex items-center gap-1 text-xs text-brand-red hover:underline mt-1"
                    onClick={(e) => e.stopPropagation()}>
                <ExternalLink className="w-3 h-3" /> Schülerakte öffnen
              </Link>
            </div>

            {loading && <p className="text-xs text-slate-400">Lade Details…</p>}

            {category === 'missing_payer' && !loading && (
              <div className="space-y-3">
                <Button type="button" variant="outline" size="sm" disabled={saving} onClick={linkSiblingPayer}
                        data-testid="dq-link-sibling-payer">
                  Zahler von Geschwister übernehmen
                </Button>
                {guardians.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-600">Verknüpfte Angehörige</p>
                    {guardians.map((sg) => {
                      const g = sg.guardian;
                      if (!g) return null;
                      return (
                        <div key={sg.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5 text-xs">
                          <span>
                            {g.last_name}, {g.first_name}
                            {g.is_payer ? <span className="ml-1 text-emerald-700">(bereits Zahler)</span> : null}
                          </span>
                          {!g.is_payer && (
                            <Button type="button" size="sm" className="h-7 bg-brand-red hover:bg-brand-red/90"
                                    disabled={saving} onClick={() => markPayer(g.id)}>
                              Als Zahler
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-brand-red">Keine Angehörigen verknüpft — bitte suchen und zuordnen.</p>
                )}
                <div className="flex gap-2">
                  <Input value={search} onChange={(e) => setSearch(e.target.value)}
                         placeholder="Angehörigen suchen…" className="h-8 text-xs"
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchGuardians(); } }} />
                  <Button type="button" size="sm" variant="outline" onClick={searchGuardians}><Search className="w-3.5 h-3.5" /></Button>
                </div>
                {searchHits.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded text-xs divide-y">
                    {searchHits.map((g) => (
                      <button key={g.id} type="button" disabled={saving}
                              className="w-full text-left px-2 py-1.5 hover:bg-slate-50"
                              onClick={() => linkGuardian(g.id)}>
                        {g.last_name}, {g.first_name}{g.is_payer ? ' · Zahler' : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {category === 'missing_family' && !loading && (
              <div className="space-y-3">
                <Button type="button" className="bg-brand-red hover:bg-brand-red/90" size="sm"
                        disabled={saving} onClick={createFamily} data-testid="dq-create-family">
                  Neue Familie „Familie {detail?.last_name || current.name}“ anlegen
                </Button>
                <div className="flex gap-2">
                  <Input value={search} onChange={(e) => setSearch(e.target.value)}
                         placeholder="Bestehende Familie suchen…" className="h-8 text-xs"
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchFamilies(); } }} />
                  <Button type="button" size="sm" variant="outline" onClick={searchFamilies}><Search className="w-3.5 h-3.5" /></Button>
                </div>
                {searchHits.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded text-xs divide-y">
                    {searchHits.map((f) => (
                      <button key={f.id} type="button" disabled={saving}
                              className="w-full text-left px-2 py-1.5 hover:bg-slate-50"
                              onClick={() => addToFamily(f.id)}>
                        {f.name}{f.primary_city ? ` · ${f.primary_city}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {category === 'missing_contract' && !loading && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Aktiven Vertrag mit Tarif anlegen und zum nächsten Schüler wechseln.</p>
                <select value={tariffId || suggestedTariffId} onChange={(e) => setTariffId(e.target.value)}
                        className="w-full h-8 rounded-md border border-slate-300 text-xs px-2"
                        data-testid="dq-contract-tariff">
                  <option value="">Tarif wählen…</option>
                  {tariffs.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} — {Number(t.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    </option>
                  ))}
                </select>
                <Button type="button" className="bg-brand-red hover:bg-brand-red/90" size="sm"
                        disabled={saving || !(tariffId || suggestedTariffId)} onClick={createContract}
                        data-testid="dq-create-contract">
                  Vertrag anlegen &amp; weiter
                </Button>
              </div>
            )}

            <div className="flex justify-between pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={skip} data-testid="dq-wizard-skip">
                <SkipForward className="w-3.5 h-3.5 mr-1" /> Überspringen
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Schließen</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
